import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Plus, Home, MapPin, ClipboardList, ArrowRight, ShieldCheck, X, FileText, Users, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { api, formatError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * /portfolio — Property Passport professional dashboard
 * Portfolio of properties + click into per-property Passport view.
 */
export default function PortfolioDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?redirect=/portfolio');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([api.pnPortfolioSummary(), api.pnListProperties()]);
      setSummary(s.data);
      setProperties(p.data || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="portfolio-dashboard-page">
      <Header />
      <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Property Passport</div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A] md:text-5xl">Portfolio Dashboard</h1>
            <p className="mt-3 max-w-2xl text-sm text-[#5C5B5A]">Every property you own or manage. Click a property to open its full Passport — jobs, quotes, warranties, compliance and audit trail.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/portfolio/panel" className="btn-ghost" data-testid="portfolio-open-panel">
              <Users size={14} /> Panel
            </Link>
            <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-property-btn">
              <Plus size={16} /> Add property
            </button>
          </div>
        </div>

        {/* Stats strip */}
        {summary && (
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Properties" value={summary.properties} icon={Building2} data-testid="stat-properties" />
            <Stat label="Total jobs" value={summary.total_jobs} icon={ClipboardList} data-testid="stat-total-jobs" />
            <Stat label="Open jobs" value={summary.open_jobs} icon={FileText} data-testid="stat-open-jobs" />
            <Stat label="Compliance alerts" value={summary.compliance_alerts} icon={ShieldCheck} data-testid="stat-compliance-alerts" />
          </div>
        )}

        {/* Properties grid */}
        <section className="mt-12">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]">Your properties</div>
              <h2 className="mt-2 font-heading text-2xl font-bold text-[#1C1B1A]">Portfolio</h2>
            </div>
          </div>

          {loading ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-10 text-center text-sm text-[#8A8988]" data-testid="portfolio-loading">Loading portfolio…</div>
          ) : properties.length === 0 ? (
            <div className="border border-dashed border-[#E5E4E0] bg-white p-12 text-center" data-testid="portfolio-empty">
              <Home size={32} className="mx-auto text-[#8A8988]" />
              <h3 className="mt-4 font-heading text-xl font-bold text-[#1C1B1A]">No properties yet</h3>
              <p className="mt-2 text-sm text-[#5C5B5A]">Add your first property to start building its Passport.</p>
              <button onClick={() => setShowAdd(true)} className="btn-primary mt-6 inline-flex" data-testid="portfolio-empty-add-btn">
                <Plus size={14} /> Add first property
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {properties.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p.id)}
                  className="group border border-[#E5E4E0] bg-white p-6 text-left transition-all hover:border-[#1A362D] hover:shadow-md"
                  data-testid={`property-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-10 w-10 items-center justify-center bg-[#1A362D] text-white">
                      <Home size={18} />
                    </div>
                    <div className="rounded-full border border-[#1A362D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#1A362D]">
                      {p.property_type}
                    </div>
                  </div>
                  <div className="mt-4 font-heading text-lg font-bold text-[#1C1B1A]">
                    {p.label || p.address_line}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-[#8A8988]">
                    <MapPin size={12} /> {p.suburb} {p.postcode}
                  </div>
                  <div className="mt-5 flex items-center justify-between border-t border-[#E5E4E0] pt-4">
                    <div className="text-xs text-[#5C5B5A]">
                      <span className="font-semibold text-[#1C1B1A]">{p.job_count}</span> job{p.job_count === 1 ? '' : 's'}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8A8988]">Score</div>
                      <div className="flex h-6 w-14 items-center overflow-hidden border border-[#E5E4E0] bg-[#F3F2EE]">
                        <div className="h-full bg-[#1A362D]" style={{ width: `${p.passport_score}%` }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#1A362D] group-hover:gap-3">
                    Open Passport <ArrowRight size={12} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {showAdd && <AddPropertyModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {selected && <PropertyPassportModal propertyId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon, ...rest }) {
  return (
    <div className="border border-[#E5E4E0] bg-white p-5" {...rest}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">{label}</div>
        <Icon size={14} className="text-[#1A362D]" />
      </div>
      <div className="mt-2 font-heading text-3xl font-bold text-[#1C1B1A]">{value ?? '—'}</div>
    </div>
  );
}

function AddPropertyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ address_line: '', suburb: '', postcode: '', property_type: 'house', label: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address_line || !form.suburb || !form.postcode) {
      toast.error('Please complete address, suburb and postcode.');
      return;
    }
    setBusy(true);
    try {
      await api.pnCreateProperty(form);
      toast.success('Property added to portfolio.');
      onCreated();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-property-modal">
      <div className="w-full max-w-lg border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Property Passport</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Add a property</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-add-property"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-6">
          <Field label="Street address">
            <input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="42 High Street" className="input-base" data-testid="input-address-line" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Suburb">
              <input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} placeholder="Fremantle" className="input-base" data-testid="input-suburb" />
            </Field>
            <Field label="Postcode">
              <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="6160" maxLength={4} className="input-base" data-testid="input-postcode" />
            </Field>
          </div>
          <Field label="Property type">
            <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="input-base" data-testid="input-property-type">
              <option value="house">House</option>
              <option value="unit">Unit</option>
              <option value="townhouse">Townhouse</option>
              <option value="commercial">Commercial</option>
              <option value="strata_common">Strata common area</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Friendly label (optional)">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Beach shack, Rental #1…" className="input-base" data-testid="input-label" />
          </Field>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="submit-add-property">
            {busy ? 'Adding…' : 'Add property'} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C5B5A]">{label}</div>
      {children}
    </label>
  );
}

function PropertyPassportModal({ propertyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    api.pnPropertyPassport(propertyId).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e))).finally(() => setLoading(false));
  }, [propertyId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-10" data-testid="property-passport-modal">
      <div className="w-full max-w-4xl border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Property Passport</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">
              {loading ? 'Loading…' : (data?.property?.label || data?.property?.address_line)}
            </h3>
            {data?.property && (
              <div className="mt-1 flex items-center gap-1 text-xs text-[#8A8988]">
                <MapPin size={12} /> {data.property.address_line}, {data.property.suburb} {data.property.postcode}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {data?.property && (
              <button onClick={() => setShowShare(true)} className="btn-ghost !py-1 !px-3 !text-xs" data-testid="passport-share-btn">
                <Share2 size={12} /> Share
              </button>
            )}
            <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-passport-modal"><X size={18} /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#8A8988]">Loading Passport…</div>
        ) : (
          <div className="p-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <StatMini label="Total jobs" value={data.stats.total_jobs} />
              <StatMini label="Completed" value={data.stats.completed_jobs} />
              <StatMini label="Categories" value={data.stats.categories_used.length} />
            </div>
            {/* Timeline */}
            <div className="mt-8">
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#1A362D]">Maintenance timeline</div>
              <h4 className="mt-1 font-heading text-lg font-bold text-[#1C1B1A]">Every job on this property</h4>
              {data.timeline.length === 0 ? (
                <div className="mt-4 border border-dashed border-[#E5E4E0] bg-[#FAF9F6] p-8 text-center text-sm text-[#5C5B5A]" data-testid="passport-empty">
                  No jobs yet. Once tradies log work at this address, the Passport will build automatically.
                </div>
              ) : (
                <ol className="mt-4 border-l-2 border-[#E5E4E0] pl-6">
                  {data.timeline.map((e, i) => (
                    <li key={i} className="relative mb-6" data-testid={`timeline-event-${i}`}>
                      <div className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#1A362D]" />
                      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">
                        {new Date(e.date).toLocaleDateString('en-AU')} · {e.category}
                      </div>
                      <div className="mt-1 font-semibold text-[#1C1B1A]">{e.title}</div>
                      <div className="mt-1 text-xs text-[#5C5B5A]">Stage: {e.stage}</div>
                      <Link to={`/pn/jobs/${e.job_id}`} onClick={onClose} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#1A362D] hover:gap-2">
                        Open job <ArrowRight size={11} />
                      </Link>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </div>
      {showShare && <ShareModal propertyId={propertyId} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="border border-[#E5E4E0] bg-[#FAF9F6] p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-[#1C1B1A]">{value}</div>
    </div>
  );
}

function ShareModal({ propertyId, onClose }) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(30);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.pnCreateShare(propertyId, { expires_days: Number(days), label: label || undefined });
      setShare(r.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = share ? `${window.location.origin}${share.share_path}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied.');
    } catch {
      toast.error('Could not copy — select and copy manually.');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="share-modal">
      <div className="w-full max-w-md border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Property Passport · Share</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Create a public share link</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-share-modal"><X size={18} /></button>
        </div>
        <div className="space-y-4 p-6">
          {!share ? (
            <>
              <p className="text-sm text-[#5C5B5A]">
                Anyone with the link can view the read-only maintenance history. Perfect for showing a buyer, insurer, or family member. The link auto-expires and can be revoked at any time.
              </p>
              <label className="block">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C5B5A]">Optional label</div>
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Buyer inspection" className="input-base" data-testid="share-label-input" maxLength={120} />
              </label>
              <label className="block">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C5B5A]">Expires in</div>
                <select value={days} onChange={(e) => setDays(e.target.value)} className="input-base" data-testid="share-expires-input">
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>6 months</option>
                </select>
              </label>
              <button onClick={create} disabled={busy} className="btn-primary w-full justify-center" data-testid="share-create-btn">
                {busy ? 'Creating…' : 'Create share link'} <ArrowRight size={14} />
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#1A362D]">Share link ready</div>
              <div className="flex items-center gap-2 border border-[#E5E4E0] bg-[#FAF9F6] p-3">
                <input value={shareUrl} readOnly className="flex-1 bg-transparent text-xs text-[#1C1B1A] outline-none" data-testid="share-url-input" />
                <button onClick={copy} className="btn-primary !py-1 !px-3 !text-xs" data-testid="share-copy-btn">
                  <Copy size={12} /> Copy
                </button>
              </div>
              <div className="text-xs text-[#5C5B5A]">Expires {new Date(share.expires_at).toLocaleString('en-AU')}</div>
              <button onClick={onClose} className="btn-ghost w-full justify-center" data-testid="share-done-btn">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
